/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { removeRecentSearch } from 'bundles/search-common/utils/SearchUtils';
import type { AutocompleteItems } from 'bundles/search-common/utils/autocomplete';
import { getAutocompleteEventingData } from 'bundles/search-common/utils/autocomplete';

const styles = {
  optionContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% + var(--cds-spacing-200)); /* Extend beyond parent by 16px total (8px each side) */
    position: relative;
    min-height: 24px;
    padding: 0 var(--cds-spacing-200) !important;
    margin-left: calc(-1 * var(--cds-spacing-100)) !important;
    margin-right: calc(-1 * var(--cds-spacing-100)) !important;

    &:hover .clear-icon {
      opacity: 1;
    }

    /* On desktop: Show button on hover or when parent has focus class */
    @media (min-width: 1061px) {
      .clear-icon-button {
        opacity: 0;
      }

      &:hover .clear-icon-button,
      .clear-icon-button:focus {
        opacity: 1;
      }
    }

    /* On mobile/tablet: Always visible */
    @media (max-width: 1060px) {
      .clear-icon-button {
        opacity: 1;
      }
    }
  `,

  clearIcon: css`
    opacity: 0;
    transition: opacity 0.2s ease;
    margin-right: calc(-1 * var(--cds-spacing-50));
    flex-shrink: 0;

    &:hover,
    &:focus {
      opacity: 1;
    }
  `,
};

type Props = {
  children: React.ReactNode;
  autocompleteState: AutocompleteMode;
  id: string;
  recentlyViewedItems: AutocompleteItems;
  suggestionIndex?: number;
  onRemove?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
};

const AutocompleteRecentlySearchedOption = ({
  children,
  autocompleteState,
  id,
  recentlyViewedItems,
  suggestionIndex,
  onRemove,
  inputRef,
}: Props) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const trackingData = getAutocompleteEventingData(id, autocompleteState, recentlyViewedItems, { suggestionIndex });
  const trackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  // Extract search term from children (it's a string)
  const searchTerm = typeof children === 'string' ? children : '';

  React.useEffect(() => {
    // Handle right arrow to focus X button
    // Note: This allows keyboard users to access the remove button via Right Arrow key.
    // CONS/LIMITATIONS:
    // - After focusing the button, CDS Autocomplete loses track of the active item state
    // - Using Tab/Arrow keys from the button breaks out of autocomplete navigation
    // - Users must click back into the input to resume normal autocomplete arrow navigation
    // - This is a fundamental limitation of CDS Autocomplete's focus management (uses aria-activedescendant pattern)
    // Alternative approaches attempted but failed:
    // - Returning focus programmatically to input breaks CDS internal state
    // - Can't wrap Autocomplete.Group (CDS requires direct children)
    // - className/css props not supported on Autocomplete.Group
    const container = trackingRef.current;
    const inputElement = inputRef?.current;

    // Early returns should not register cleanup - just return undefined
    if (!container || !inputElement) {
      return undefined;
    }

    const handleKeyDown = (e: Event) => {
      const keyboardEvent = e as KeyboardEvent;
      const parentOption = container.closest('[role="option"]');
      if (!parentOption) return;

      // Check if this option is currently focused
      const isFocused = parentOption.classList.contains('cds-AutocompleteOption-focused');

      if (isFocused && keyboardEvent.key === 'ArrowRight') {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        buttonRef.current?.focus();
      }
    };

    inputElement.addEventListener('keydown', handleKeyDown, true);
    return () => {
      inputElement.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [trackingRef, inputRef]);

  const handleRemove = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      if (searchTerm) {
        removeRecentSearch(searchTerm);
        onRemove?.();
      }
    },
    [searchTerm, onRemove]
  );

  const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        if (searchTerm) {
          removeRecentSearch(searchTerm);
          onRemove?.();
        }
      }
    },
    [searchTerm, onRemove]
  );

  const handleMouseDown = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
  }, []);

  return (
    <div ref={trackingRef} css={styles.optionContainer}>
      <TrackedDiv withVisibilityTracking requireFullyVisible trackingName="autocomplete_item">
        {children}
      </TrackedDiv>
      <div
        role="presentation"
        css={css`
          display: inline-flex;
          align-items: center;
          pointer-events: auto;
          height: 24px;
          flex-shrink: 0;
        `}
      >
        <IconButton
          ref={buttonRef}
          intent="close"
          size="small"
          variant="ghostSecondary"
          onClick={handleRemove}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          onMouseUp={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          aria-label={`Remove ${searchTerm} from recent searches`}
          data-testid="clear-recent-search"
          className="clear-icon-button"
        />
      </div>
    </div>
  );
};

export default AutocompleteRecentlySearchedOption;
